import {
  LockScreen,
  LockScreenMode,
  useIdleLock,
  useMediaQuery,
} from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useActivityHeartbeat } from "../../hooks/useActivityHeartbeat";
import { useLockScreenTexts } from "../../hooks/useLockScreenTexts";
import { usePinErrorHandler } from "../../hooks/usePinErrorHandler";
import { pinApi } from "../../services/pin.api";
import { sessionApi } from "../../services/session.api";
import {
  getSessionSuccess,
  pinAccepted,
  requestTerminateSessionRequest,
  selectHasPin,
  selectPinLockMinutes,
  selectSessionLocked,
  selectSecurityLogout,
  sessionLocked,
} from "../../store/reducers/sessionSlice";
import { RootState } from "../../store/types";
import { PinLockContext } from "./pinLockContext";
import { SecurityLogoutNotice } from "./SecurityLogoutNotice";
import { PinResetRequestResponse } from "../../types/pin.definition";
import {
  clearPersistedPinLock,
  getErrorCode,
  PIN_LOCK_STORAGE_KEY,
} from "../../utils/pin-utils";

/**
 * MED-1622: PIN lock of the session, enforced by the server.
 *
 * - `hasPin === false` (session payload or a 403 PIN_REQUIRED): blocking
 *   "create your PIN" screen, nothing else is rendered.
 * - `hasPin === true`: the screen locks after the user's lock delay
 *   (`pinLockMinutes`, 15 minutes by default — MED-1644) without activity (in
 *   any tab), when the session payload says `locked`, or on any 423
 *   SESSION_LOCKED. The unlock screen covers the page, which stays mounted.
 * - `hasPin` absent (API older than the PIN feature): the lock stays off.
 */
export const PinLockProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const handlePinError = usePinErrorHandler();
  const signedIn = useSelector(
    (state: RootState) => !!state.session.session?.user
  );
  const fullName = useSelector((state: RootState) => {
    const user = state.session.session?.user;
    return [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  });
  const hasPin = useSelector(selectHasPin);
  const pinLockMinutes = useSelector(selectPinLockMinutes);
  const serverLocked = useSelector(selectSessionLocked);
  const securityLogout = useSelector(selectSecurityLogout);

  // Same breakpoint as the LockScreen's desktop layout, which signs out
  // through its own link instead of the cancel one.
  const isDesktop = useMediaQuery("(min-width: 640px)");

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetInfo, setResetInfo] = useState<PinResetRequestResponse | null>(
    null
  );

  const lockOn = signedIn && hasPin === true;
  const onLock = useCallback(() => setError(null), []);
  const idle = useIdleLock({
    // Set here rather than relying on the design system's default.
    timeoutMs: pinLockMinutes * 60_000,
    enabled: lockOn,
    storageKey: PIN_LOCK_STORAGE_KEY,
    onLock,
  });
  const { lock: idleLock, locked: idleLocked } = idle;

  // The server lock goes through the idle lock, so every tab locks with it.
  useEffect(() => {
    if (lockOn && serverLocked) idleLock();
  }, [lockOn, serverLocked, idleLock]);

  // Unlocked from another tab: this tab's server flag is stale.
  const serverLockedRef = useRef(serverLocked);
  serverLockedRef.current = serverLocked;
  const wasIdleLocked = useRef(idleLocked);
  useEffect(() => {
    if (wasIdleLocked.current && !idleLocked && serverLockedRef.current) {
      dispatch(pinAccepted());
    }
    wasIdleLocked.current = idleLocked;
  }, [idleLocked, dispatch]);

  let mode: LockScreenMode | null = null;
  if (signedIn && hasPin === false) mode = "create";
  else if (lockOn && (idleLocked || serverLocked))
    mode = resetInfo ? "reset" : "unlock";

  // Keeps the server from locking a user who is active without API calls.
  useActivityHeartbeat(lockOn && mode === null && !securityLogout);

  // A session locked from the start never showed its page: keep it unmounted
  // until unlocked, its calls would only answer 423.
  const contentShown = useRef(false);
  if (mode === null) contentShown.current = true;
  const renderContent =
    mode === null || (mode !== "create" && contentShown.current);

  const accept = async () => {
    // GET /session is partial (identity only) while locked or without a PIN:
    // reload the full payload before the page shows again.
    try {
      dispatch(getSessionSuccess(await sessionApi.getSession()));
    } catch {
      // The PIN was accepted: the page reloads what it needs on its own.
    }
    dispatch(pinAccepted());
    idle.unlock();
    setResetInfo(null);
    setError(null);
  };

  const run = async (call: () => Promise<unknown>) => {
    try {
      await call();
    } catch (e) {
      setError(handlePinError(e));
      // Created meanwhile (another tab): ask for it instead.
      if (getErrorCode(e) === "PIN_ALREADY_SET") dispatch(sessionLocked());
      throw e;
    }

    await accept();
  };

  const onForgot = async () => {
    setBusy(true);
    try {
      setResetInfo(await pinApi.requestReset());
      setError(null);
    } catch (e) {
      setError(handlePinError(e));
    } finally {
      setBusy(false);
    }
  };

  const texts = useLockScreenTexts({
    otpDescription: resetInfo
      ? t(`pinLock.otpSent.${resetInfo.channel}`, {
          destination: resetInfo.destinationMasked,
        })
      : undefined,
    cancel: mode === "reset" ? t("pinLock.back") : t("pinLock.logout"),
  });

  const logout = () => {
    clearPersistedPinLock();
    dispatch(requestTerminateSessionRequest());
  };

  const back = () => {
    setResetInfo(null);
    setError(null);
  };

  // Session closed by the server for a security reason: nothing else shows.
  if (securityLogout) return <SecurityLogoutNotice code={securityLogout} />;

  return (
    <PinLockContext.Provider value={mode !== null}>
      {renderContent && children}
      {mode && (
        <LockScreen
          mode={mode}
          texts={texts}
          error={error}
          busy={busy}
          onCreate={pin => run(() => pinApi.createPin(pin))}
          onUnlock={pin => run(() => pinApi.unlock(pin))}
          onReset={(otp, newPin) => run(() => pinApi.confirmReset(otp, newPin))}
          onForgot={mode === "unlock" ? () => void onForgot() : undefined}
          onCancel={mode === "reset" ? back : isDesktop ? undefined : logout}
          onSignOut={logout}
          subtitle={
            fullName ? t("pinLock.signedInAs", { name: fullName }) : undefined
          }
        />
      )}
    </PinLockContext.Provider>
  );
};
